import { useMutation } from '@tanstack/react-query';
import { getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ComponentProps, ReactNode, useMemo } from 'react';

import { useSession } from '@/auth/useSession';
import { allCoachColumns, type CoachSessionsTableColumnId } from '@/components/session/sessions-table-columns';
import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table';
import { attendSession } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { CoachSession } from '@airreading/common/data';

export type EntityTableProps = ComponentProps<typeof EntityTable>;

export interface CoachSessionsTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessions: CoachSession[];
  errorState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  filters?: EntityTableProps['filters'];
  tableHookProps?: Omit<TableOptions<CoachSession>, 'columns' | 'data' | 'getCoreRowModel'>;
  hideColumnIds?: Array<CoachSessionsTableColumnId>;
  tableTestId?: string;
  rowTestId?: (rowId: string, rowIndex: number) => string;
  cellTestId?: (rowId: string, columnId: string, rowIndex: number) => string | undefined;
  tablePrefix?: string;
  groupBy?: EntityTableGroupBy<CoachSession, GroupKey>;
}

export const CoachSessionsTable = <GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessions,
  errorState,
  filters,
  hasError,
  isLoading,
  tableHookProps,
  hideColumnIds = [],
  groupBy,
  tableTestId,
  rowTestId,
  cellTestId,
  tablePrefix,
}: CoachSessionsTableProps<GroupKey>) => {
  const { actingUser } = useSession();
  const { mutateAsync: attendSessionMutate } = useMutation({
    mutationFn: attendSession,
  });

  const columns = useMemo(
    // column is kept if it does not have an id (cannot be hidden) or if it is not in the hideColumnIds
    () =>
      allCoachColumns({
        actingUser,
        onStartOrJoinSessionButtonClick: async (row) => {
          await attendSessionMutate({ id: row.id });
        },
        tablePrefix,
      }).filter((c) => !c.id || !(hideColumnIds as string[]).includes(c.id)),
    [hideColumnIds, actingUser, attendSessionMutate, tablePrefix],
  );

  const { rowSelection, onRowSelectionChange, getRowId } = useRowsBulkSelection<CoachSession>({
    getRowId: (row) => row.id,
    rowIdToData: (id) => sessions.find((session) => session.id === id),
  });

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    getRowId,
    onRowSelectionChange,
    ...tableHookProps,
    defaultColumn: {
      enableSorting: false,
      ...tableHookProps?.defaultColumn,
    },
    state: {
      rowSelection,
      ...tableHookProps?.state,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState={'No sessions found'}
      errorState={errorState}
      isLoading={isLoading}
      hasError={hasError}
      filters={filters}
      showPagination={true}
      data-testid={tableTestId}
      rowTestId={rowTestId}
      cellTestId={cellTestId}
      groupBy={groupBy}
    />
  );
};
